import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import AIFunnelVisualization from './AIFunnelVisualization';
import VideoModal from './VideoModal';

interface HeroSectionProps {
  onOpenDemoModal: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onOpenDemoModal }) => {
  const navigate = useNavigate();
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-7xl mx-auto">
        <div 
          className="relative bg-bg-surface backdrop-blur-sm border border-card-border rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-16 shadow-hero-inner"
        >
          {/* Top Row */}
          <div className="flex items-center justify-between mb-8 sm:mb-12 lg:mb-20">
            <div className="flex items-center space-x-3">
              <img 
                src="/landing-page/salespad logo.svg" 
                alt="SalesPad Logo" 
                className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10"
              />
              <div className="text-text-primary font-bold text-xl sm:text-2xl lg:text-3xl font-inter">
                SalesPad
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => window.location.href = 'https://ai.salespad.app/'}
                className="hidden sm:block px-4 py-2 text-text-secondary hover:text-text-primary font-medium text-sm transition-all duration-300 hover:bg-bg-surface rounded-lg border border-transparent hover:border-card-border"
              >
                Login
              </button>
              <button
                onClick={onOpenDemoModal}
                className="px-3 py-2 sm:px-5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-cta text-cta-text font-bold text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 cta-hover-overlay"
              >
                Book a demo
              </button>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            {/* Left Column - Copy */}
            <div className="space-y-4 sm:space-y-6 lg:space-y-8 text-center lg:text-left">
              {/* Headline */}
              <h1 className="text-text-primary font-black text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-tight font-inter">
                <div className="gradient-text">AI Sales Team</div>
                <div>Always On</div>
                <div>Always Selling</div>
              </h1>

              {/* Subcopy */}
              <p className="text-text-secondary text-sm sm:text-lg lg:text-xl xl:text-2xl max-w-2xl leading-relaxed mx-auto lg:mx-0 text-center lg:text-left">
                <span className="lg:hidden">Finding, engaging and bookings prospects 24/7 on autopilot</span>
                <span className="hidden lg:inline">
                  Finding, engaging and bookings prospects<br />
                  24/7 on autopilot
                </span>
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={onOpenDemoModal}
                  className="px-4 py-3 sm:px-5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-cta text-cta-text font-bold text-base sm:text-lg transition-all duration-300 hover:shadow-xl hover:scale-105 cta-hover-overlay text-center"
                >
                  Book a live demo
                </button>
                <button
                  onClick={() => setIsVideoOpen(true)}
                  className="px-4 py-3 sm:px-5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-bg-surface border border-card-border text-text-primary font-medium text-base sm:text-lg transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3">
                  <Play className="w-4 h-4 sm:w-5 sm:h-5" />
                  Watch 60s overview
                </button>
              </div>

            </div>

            {/* Right Column - Visual */}
            <div className="relative flex justify-center lg:justify-end mt-6 lg:mt-0">
              <AIFunnelVisualization />
            </div>
          </div>
        </div>
      </div>

      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoId="AjnWKYhq7-w"
      />
    </div>
  );
};

export default HeroSection;
